/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState, useCallback } from 'react';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Search from '../../icons/Search.svg?react'
import Close from '../../icons/Close.svg?react';
import GenericMikeTable from './GenericMikeTable';
import { Typography } from '@mui/material';
import { orderBy } from 'lodash';
import MIKE_COLORS from '../../styles/mike-colors';
import PlayFilled from '../../icons/PlayFilled.svg?react';
import Plus from '../../icons/Plus.svg?react';
import mikeSharedTheme, { mikePalette } from '../../styles/mikeSharedTheme';
import MikeButton from '../mike-button';

interface IProps {
  rows: Array<any>; 
  loading: boolean;
  onSelect?: (row: any) => void; 
  onAdd?: () => void;
  onStart?: () => void;
  title?: string;
  searchPlaceHolder?: string;
  tooltipAddButton?: string;
  addButtonText?: string;
  startButtonText?: string;
  config: any; 
  columns: any; 
  onUpdate?: (row: any) => void; 
  onDelete?: (row: any) => void; 
  actions?: any; 
  maxHeight?: string;
  canEditFolder?: boolean;
}

const containerStyle = css`
  background: ${MIKE_COLORS.XLIGHTGREY};
  margin-top: 32px;
  box-sizing: content-box;
`;
const containerNoMarginsStyle = css`
  background: ${MIKE_COLORS.XLIGHTGREY};
  box-sizing: content-box;
`;
const tableContainerStyle = css`
  margin-top: 40px;
`;
  const tableContainerNoMarginsStyle = css`
    padding: 0;
`;
  const textFieldStyle = css`
    width: 320px;
    height: 40px;
    background: #FFFFFF;
    border-radius: 25px;
    border: 1px solid #DBE4E9 !important;
   
`;
  const titleStyle = css`
    margin: 0;
    color: ${MIKE_COLORS.BRANDBLUE_DEFAULT};   
`;

  const headStyle = css`
    padding-left: ${mikeSharedTheme.spacing(4)};
    padding-bottom: ${mikeSharedTheme.spacing(3)};    
`;

  const buttonPaddingStyle = css`
    padding-left: ${mikeSharedTheme.spacing(1)};
  `;

const iconWhiteStyle = (disabled = false) => {
  const color = disabled
  ? mikePalette.background.paper
  : mikePalette.background.default  
 
  return css`
    path {
      fill: ${color};
      stroke: ${color};
    }
  `
}

const flexStyle = css`    
  display: flex;
  justify-content: space-between;
  align-items: center;  
  width: 99vw;   

`;

const buttonStyle = css`     
  border: 2px solid ${mikePalette.secondary.main};
  color: mikePalette.background.default;
  &[disabled]: {
    color: ${mikePalette.background.paper};
    opacity: 0.5;
    cursor: not-allowed;
    border: 2px solid ${mikePalette.secondary.light};
  };
`;


const buttonsContainerStyle = css`      
  display: flex;   
  padding-right: ${mikeSharedTheme.spacing(4)};    
`; 

const iconSecondaryStyle = (disabled = false) => {
  const color = disabled
    ? mikePalette.secondary.light
    : mikePalette.secondary.main
  return css`
    path {
      fill: ${color};
      stroke: ${color};
    }
  `
}

/**
 * @name MikeTable
 * @summary A generic data table component, built on top of Material UI tables.
 */
export const MikeTable: React.FC<IProps> = ({
  columns,
  config,
  rows,
  title,
  searchPlaceHolder,
  onSelect,
  loading,
  addButtonText,
  startButtonText,
  onAdd,
  onStart,
  actions,
  maxHeight,
  canEditFolder
}) => {
  const [filter, setFilter] = useState('');
  const [filteredRows, setFilteredRows] = React.useState(rows);
  const [isSorting, setIsSorting] = React.useState(false);
  const [orderByField, setOrderByField] = React.useState({
    field: config.defaultSort.field,
    dir: 'desc',
    type: config.defaultSort.type,
  });  

  React.useEffect(() => {
    let dates: Array<any> = [];
    if (filter === '') {
      let orderedRows: Array<any> = [];
      if (orderByField.type === 'date') {
        orderedRows = rows.sort((a: any, b: any) => {
          const aDate = new Date(a[orderByField.field]);
          const bDate = new Date(b[orderByField.field]);
          return aDate.getTime() - bDate.getTime();
        });
        dates = orderByField.dir === 'desc' ? orderedRows.reverse() : orderedRows;
      } else {
        // Ignore case when ordering by string:
        const sortHandler = (row) => {
          return row[orderByField.field].toLowerCase
            ? row[orderByField.field].toLowerCase()
            : row[orderByField.field];
        };
        dates = orderBy(rows, sortHandler, [orderByField.dir as any]);
      }
      setFilteredRows(dates);
      setIsSorting(false);
      return;
    }
    const filterRows: Array<any> = [];
    for (const row of rows) {
      let contains = false;
      for (const col of columns) {
        if (contains || !col.filtrable) {
          break;
        }
        contains = false;
        const objValues: Array<string> = Object.values(row);
        for (const value of objValues) {
          if (
            value &&
            value
              .toString()
              .toLowerCase()
              .includes(filter.toLowerCase())
          ) {
            filterRows.push(row);
            contains = true;
            break;
          }
        }
      }
    }
    let orderedFilteredRows: Array<any> = [];
    if (orderByField.type === 'date') {
      orderedFilteredRows = filterRows.sort((a: any, b: any) => {
        const aDate = new Date(a[orderByField.field]);
        const bDate = new Date(b[orderByField.field]);
        return aDate.getTime() - bDate.getTime();
      });
      orderedFilteredRows =
        orderByField.dir === 'desc' ? orderedFilteredRows.reverse() : orderedFilteredRows;
    } else {
      orderedFilteredRows = orderBy(filterRows, orderByField.field, [orderByField.dir as any]);
    }
    setFilteredRows(orderedFilteredRows);
    setIsSorting(false);
  }, [columns, filter, orderByField, rows]);

  const handleClearFilter = useCallback(() => {
    setFilter('');
  }, []);

  const handleSetFilter = useCallback((e: any) => {
    setFilter(e.target.value);
  }, []);

  // TODO: Use real type iso any:
  const handleSetSorting = useCallback((param: any) => {
    setIsSorting(true);
    setOrderByField(param);
  }, []);

  return (
    <>
      <div
        css={
          config.startButton || config.addButton || config.filtered ? containerStyle : containerNoMarginsStyle
        }
      >
      
        <div css={headStyle}>         
          {title && (
            <Typography variant="h1" css={titleStyle}>
              {title}
            </Typography>
          )}       
          <div css={flexStyle}>            
              {config.filtered && (
                <Input
                  autoFocus
                  css={textFieldStyle}
                  onChange={handleSetFilter}
                  disableUnderline={true}
                  placeholder={searchPlaceHolder ? searchPlaceHolder : 'What are you looking for?'}
                  value={filter}
                  startAdornment={
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  }
                  endAdornment={
                    filter !== '' ? (
                      <InputAdornment position="end" onClick={handleClearFilter}>
                        <Close />
                      </InputAdornment>
                    ) : null
                  }
                />
              )}        
            <div css={buttonsContainerStyle}>  
              {config.startButton && (
                <div css={buttonPaddingStyle}>
                <MikeButton variant="contained" css={buttonStyle} disabled={!canEditFolder} onClick={onStart}>
                  <PlayFilled css={iconWhiteStyle(!canEditFolder)}/>
                  {startButtonText ? startButtonText : ""}
                </MikeButton>  
                </div>
              )}
              {config.addButton && (
                <div css={buttonPaddingStyle}>
                  <MikeButton variant="outlined" color="secondary" disabled={!canEditFolder} onClick={onAdd}>
                    <Plus css={iconSecondaryStyle(!canEditFolder)} />
                    {addButtonText ? addButtonText : ""}
                  </MikeButton>
                </div> 
                )}
            </div>

          </div>
        </div>      
        <div
          css={
            config.addButton || config.filtered
              ? tableContainerStyle
              : tableContainerNoMarginsStyle
          }
        >
          <GenericMikeTable
            rows={filteredRows}
            filter={filter}
            loading={loading}
            isSorting={isSorting}
            columns={columns}
            config={config}
            actions={actions}
            onSelect={onSelect}
            maxHeight={maxHeight}
            onSort={handleSetSorting}
            sortByField={orderByField}
          />
        </div>
      </div>
    </>
  );
};

export default MikeTable;
